import { type ApolloClient, type NormalizedCacheObject, gql } from '@apollo/client/core';
import type {
  ArchiveSettlementMutationInput,
  CreateSettlementMutationInput,
  Mutation,
  Query,
  QueryEventArtistSettlementsArgs,
  SaveSettlementCustomExpensesMutationInput,
  SaveSettlementDealsMutationInput,
  SaveSettlementPaymentsMutationInput,
  SaveSettlementEventsMutationInput,
  SaveSettlementManualTicketSalesMutationInput,
  SaveSettlementTaxDetailsMutationInput,
  SettlementTypeEnum,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as CustomExpense from './CustomExpense';
import * as Deal from './Deal';
import * as ManualTicketSale from './ManualTicketSale';
import * as Payment from './Payment';
import type { SettlementType } from './SettlementType';
import type { VPResult } from '../../VPResult';
import * as Settlement from './Settlement';
import * as TaxDetails from './TaxDetails';
import { SETTLEMENT_FIELDS } from './fragments';

/**
 * The settlement's read and its tax, type, events and deals writes on the tenant (AppSchema)
 * connection. The legacy equivalents are the root `eventArtistSettlement` field and one
 * fifteen-argument `updateEventArtistSettlement` patch, which the client already called as six
 * section-shaped actions; ADR-012 decision 3 replaces that patch with a named mutation per section,
 * so a failure names its section and -- because the patch was presence-gated -- a field can finally
 * be cleared.
 *
 * **Nothing here catches.** A transport failure throws and propagates; `VPResult`'s
 * `success: false` covers only the GraphQL-level outcome. That preserves the settlement modal's
 * existing `try`/`catch`-with-toast contract -- this is a transport move, not a change of failure
 * semantics.

 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

/** A write whose caller reads nothing off the payload: only the outcome and the errors matter. */
type Acknowledged = VPResult<undefined, VPErrors.VPError[]>;

export const SETTLEMENT_QUERY = gql`
  query AppEventArtistSettlement($id: Int!) {
    eventArtistSettlement(id: $id) {
      ...SettlementFields
    }
  }
  ${SETTLEMENT_FIELDS}
`;

/** Selects only what an old settlement URL needs to find its artist. */
export const SETTLEMENT_ARTIST_QUERY = gql`
  query AppSettlementArtist($id: Int!) {
    eventArtistSettlement(id: $id) {
      id
      artistId
      availableEvents {
        id
      }
    }
  }
`;

/** Selects only what the artist modal's settlement list labels a row with. */
export const EVENT_ARTIST_SETTLEMENTS_QUERY = gql`
  query AppEventArtistSettlements($eventGroupId: ID!, $artistId: ID!) {
    eventArtistSettlements(eventGroupId: $eventGroupId, artistId: $artistId) {
      id
      createdAt
      settlementType
      events {
        id
        startTime
        timezone
      }
    }
  }
`;

/** Selects the list query's row, so the tab can show the new settlement beside the others. */
export const CREATE_SETTLEMENT_MUTATION = gql`
  mutation createEventArtistSettlement($input: CreateSettlementMutationInput!) {
    createEventArtistSettlement(input: $input) {
      settlement {
        id
        createdAt
        settlementType
        events {
          id
          startTime
          timezone
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const ARCHIVE_SETTLEMENT_MUTATION = gql`
  mutation archiveEventArtistSettlement($input: ArchiveSettlementMutationInput!) {
    archiveEventArtistSettlement(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const SAVE_SETTLEMENT_TAX_DETAILS_MUTATION = gql`
  mutation saveSettlementTaxDetails($input: SaveSettlementTaxDetailsMutationInput!) {
    saveSettlementTaxDetails(input: $input) {
      settlement {
        ...SettlementFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${SETTLEMENT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/**
 * Its own document, targeting the same root field as the tax-details mutation because
 * `settlementType` is published only on `SettlementTaxDetailsInput`. It selects almost nothing: the
 * type write is a step on the way to the events write, and its caller reads no state off it.
 */
export const SAVE_SETTLEMENT_TYPE_MUTATION = gql`
  mutation saveSettlementType($input: SaveSettlementTaxDetailsMutationInput!) {
    saveSettlementTaxDetails(input: $input) {
      settlement {
        id
        settlementType
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * Selects only the id. `updateSelectedEvents` deliberately assigns no state -- its caller is a
 * watcher on the selection itself, which assigning would re-enter -- so anything more would be
 * fetched and thrown away.
 */
export const SAVE_SETTLEMENT_EVENTS_MUTATION = gql`
  mutation saveSettlementEvents($input: SaveSettlementEventsMutationInput!) {
    saveSettlementEvents(input: $input) {
      settlement {
        id
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const SAVE_SETTLEMENT_DEALS_MUTATION = gql`
  mutation saveSettlementDeals($input: SaveSettlementDealsMutationInput!) {
    saveSettlementDeals(input: $input) {
      settlement {
        ...SettlementFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${SETTLEMENT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/** Selects only the id: `savePayments` assigns no state and its callers refetch. */
export const SAVE_SETTLEMENT_PAYMENTS_MUTATION = gql`
  mutation saveSettlementPayments($input: SaveSettlementPaymentsMutationInput!) {
    saveSettlementPayments(input: $input) {
      settlement {
        id
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const SAVE_SETTLEMENT_CUSTOM_EXPENSES_MUTATION = gql`
  mutation saveSettlementCustomExpenses($input: SaveSettlementCustomExpensesMutationInput!) {
    saveSettlementCustomExpenses(input: $input) {
      settlement {
        id
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/** Selects the whole settlement: `SettlementConfig.vue`'s `onRowDelete` saves without refetching. */
export const SAVE_SETTLEMENT_MANUAL_TICKET_SALES_MUTATION = gql`
  mutation saveSettlementManualTicketSales($input: SaveSettlementManualTicketSalesMutationInput!) {
    saveSettlementManualTicketSales(input: $input) {
      settlement {
        ...SettlementFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${SETTLEMENT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const fetchSettlement = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<Settlement.Settlement | undefined> => {
  const { data } = await client.query<Query>({
    query: SETTLEMENT_QUERY,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  // `eventArtistSettlement` is nullable, and the modal relies on the absence: it renders its
  // spinner rather than an empty sheet when the settlement cannot be read.
  return data.eventArtistSettlement ? Settlement.fromGql(data.eventArtistSettlement) : undefined;
};

export type SettlementArtist = {
  artistId: string;
  eventIds: number[];
};

export const fetchSettlementArtist = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<SettlementArtist | undefined> => {
  const { data } = await client.query<Query>({
    query: SETTLEMENT_ARTIST_QUERY,
    variables: { id },
    fetchPolicy: 'network-only',
  });
  const settlement = data.eventArtistSettlement;
  if (!settlement) {
    return undefined;
  }

  return { artistId: settlement.artistId, eventIds: (settlement.availableEvents ?? []).map(event => event.id) };
};

export const fetchEventArtistSettlements = async (
  client: ApolloClient<NormalizedCacheObject>,
  variables: QueryEventArtistSettlementsArgs,
): Promise<Settlement.Settlement[]> => {
  const { data } = await client.query<Query>({
    query: EVENT_ARTIST_SETTLEMENTS_QUERY,
    variables,
    fetchPolicy: 'network-only',
  });

  return data.eventArtistSettlements.map(Settlement.fromGql);
};

export const createSettlement = async (
  client: ApolloClient<NormalizedCacheObject>,
  input: CreateSettlementMutationInput,
): Promise<VPResult<Settlement.Settlement, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: CREATE_SETTLEMENT_MUTATION,
    variables: { input },
  });

  const payload = data?.createEventArtistSettlement;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.settlement) {
    return failure('Failed to create the settlement.');
  }

  return { success: true, data: Settlement.fromGql(payload.settlement) };
};

/** Archives rather than deletes (ADR-022): the list query skips it, and support can restore it. */
export const archiveSettlement = async (
  client: ApolloClient<NormalizedCacheObject>,
  input: ArchiveSettlementMutationInput,
): Promise<Acknowledged> => {
  const { data } = await client.mutate<Mutation>({
    mutation: ARCHIVE_SETTLEMENT_MUTATION,
    variables: { input },
  });

  const payload = data?.archiveEventArtistSettlement;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.success) {
    return failure('Failed to archive the settlement.');
  }

  return { success: true, data: undefined };
};

export const saveSettlementTaxDetails = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  details: TaxDetails.TaxDetailsInput,
): Promise<VPResult<Settlement.Settlement, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: SaveSettlementTaxDetailsMutationInput = { id, taxDetails: TaxDetails.toGqlInput(details) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_TAX_DETAILS_MUTATION,
    variables: { input },
  });

  const payload = data?.saveSettlementTaxDetails;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // An empty error list with no settlement is the one shape that would otherwise read as success.
  if (!payload?.settlement) {
    return failure('Failed to save the tax details.');
  }

  return { success: true, data: Settlement.fromGql(payload.settlement) };
};

/**
 * Write the settlement type, and nothing else.
 *
 * `settlementType` is a member of `SettlementTaxDetailsInput` and of no other input, so this shares
 * that root field with `saveSettlementTaxDetails` -- and shares none of its logic. That separation
 * is not stylistic. `saveSettlementTaxDetails` always sends `businessName`, `taxId` and
 * `taxWithholdingAmount`, all three nullable and unvalidated, so routing a type switch through it
 * would null the artist's business name, tax id and withholding amount -- the last of which feeds
 * the payout -- on every switch back to ticket sales.
 *
 * Why it is needed at all: `saveSettlementEvents` cannot carry the type, and the model's
 * `before_save :process_settlement_type` blanks `event_ids` on any settlement not already recorded
 * as ticket sales. So the type has to land first. Publishing `settlementType` on
 * `SaveSettlementEventsMutationInput` would retire this function; that is a follow-up on the
 * schema, owed but not owned here.
 */
export const saveSettlementType = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  settlementType: SettlementType,
): Promise<Acknowledged> => {
  // Only the enum crosses the schema boundary; casting the whole literal would drop the
  // excess-property check that keeps this payload down to its one key.
  const input: SaveSettlementTaxDetailsMutationInput = {
    id,
    taxDetails: { settlementType: settlementType as SettlementTypeEnum },
  };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_TYPE_MUTATION,
    variables: { input },
  });

  const payload = data?.saveSettlementTaxDetails;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // Same shape the two settlement-returning writes guard against: an empty error list with no
  // settlement would otherwise read as success, and here that would leave the caller's optimistic
  // `settlementType` standing against a server that never took the write.
  if (!payload?.settlement) {
    return failure('Failed to save the settlement type.');
  }

  return { success: true, data: undefined };
};

export const saveSettlementEvents = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  eventIds: number[],
): Promise<Acknowledged> => {
  const input: SaveSettlementEventsMutationInput = { id, eventIds };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_EVENTS_MUTATION,
    variables: { input },
  });

  // The resolver rejects the whole write when any id is not among the settlement's available
  // events -- a failure mode the legacy patch did not have.
  const payload = data?.saveSettlementEvents;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.settlement) {
    return failure('Failed to save the selected events.');
  }

  return { success: true, data: undefined };
};

export const saveSettlementDeals = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  deals: Deal.DealInput[],
): Promise<VPResult<Settlement.Settlement, VPErrors.VPError[]>> => {
  const input: SaveSettlementDealsMutationInput = { id, deals: deals.map(Deal.toGqlInput) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_DEALS_MUTATION,
    variables: { input },
  });

  const payload = data?.saveSettlementDeals;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.settlement) {
    return failure('Failed to save the deals.');
  }

  return { success: true, data: Settlement.fromGql(payload.settlement) };
};

export const saveSettlementPayments = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  payments: Payment.PaymentInput[],
): Promise<Acknowledged> => {
  const input: SaveSettlementPaymentsMutationInput = { id, payments: payments.map(Payment.toGqlInput) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_PAYMENTS_MUTATION,
    variables: { input },
  });

  const payload = data?.saveSettlementPayments;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.settlement) {
    return failure('Failed to save the payments.');
  }

  return { success: true, data: undefined };
};

export const saveSettlementCustomExpenses = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  customExpenses: CustomExpense.CustomExpenseInput[],
): Promise<Acknowledged> => {
  const input: SaveSettlementCustomExpensesMutationInput = {
    id,
    customExpenses: customExpenses.map(CustomExpense.toGqlInput),
  };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_CUSTOM_EXPENSES_MUTATION,
    variables: { input },
  });

  const payload = data?.saveSettlementCustomExpenses;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.settlement) {
    return failure('Failed to save the custom expenses.');
  }

  return { success: true, data: undefined };
};

/** Rows and type in one save, as the legacy patch did; a rejected row persists neither. */
export const saveSettlementManualTicketSales = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  manualTicketSales: ManualTicketSale.ManualTicketSaleInput[],
  settlementType?: SettlementType,
): Promise<VPResult<Settlement.Settlement, VPErrors.VPError[]>> => {
  const input: SaveSettlementManualTicketSalesMutationInput = {
    id,
    manualTicketSales: manualTicketSales.map(ManualTicketSale.toGqlInput),
  };

  if (settlementType) {
    input.settlementType = settlementType as SettlementTypeEnum;
  }

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_SETTLEMENT_MANUAL_TICKET_SALES_MUTATION,
    variables: { input },
  });

  const payload = data?.saveSettlementManualTicketSales;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.settlement) {
    return failure('Failed to save the manual ticket sales.');
  }

  return { success: true, data: Settlement.fromGql(payload.settlement) };
};
